import {
  Box,
  Button,
  LinearProgress,
  Tooltip,
  Typography,
} from '@mui/material';

import type { MergeCountsDto } from '../api/mergeResolution.types';
import { progress } from '../state/labels';

interface FooterBarProps {
  gate: { enabled: boolean; reasons: string[] };
  counts?: MergeCountsDto;
  submitting: boolean;
  onCancel: () => void;
  onSubmit: () => void;
}

const FooterBar = ({
  gate,
  counts,
  submitting,
  onCancel,
  onSubmit,
}: FooterBarProps) => {
  const progressValue = counts ? progress(counts) : 0;
  const remaining = counts?.Remaining ?? 0;

  return (
    <Box
      component="footer"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 3,
        padding: 2,
        // The app's fixed go-to-top button (40px wide, 25px from the right
        // edge) floats over this corner once the page scrolls, so keep the
        // buttons clear of it.
        paddingRight: '80px',
        // The app's fixed copyright bar (~56px) sits over the bottom of the
        // page. Two bar heights of clear space under the buttons guarantees
        // there is always enough scroll to bring them fully into view.
        paddingBottom: '112px',
        borderTop: '1px solid #e0e0e0',
      }}
    >
      {/*
        The legend used to live here. It now sits beside each section's
        Take Mine / Take Theirs buttons, next to the cells whose colours it
        explains, so the footer carries only the decision to submit.
      */}
      <Box sx={{ flex: 1 }} />

      {/* Remaining conflicts and progress, immediately left of the buttons:
          the number the Submit decision turns on, read right before it. */}
      <Box sx={{ flex: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant="body2">
          {remaining === 0
            ? 'No remaining conflicts'
            : `Remaining conflicts: ${remaining}`}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={progressValue * 100}
          sx={{ minWidth: 200 }}
        />
      </Box>

      {/* Right: Cancel and Submit buttons */}
      <Box sx={{ display: 'flex', gap: 1, flex: 0 }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          aria-label="Cancel resolution"
          disabled={submitting}
        >
          Cancel
        </Button>
        <Tooltip
          title={gate.reasons.length > 0 ? gate.reasons.join('; ') : ''}
          arrow
        >
          <span>
            <Button
              variant="contained"
              color="primary"
              onClick={onSubmit}
              aria-label="Submit resolution"
              disabled={!gate.enabled || submitting}
            >
              Submit
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default FooterBar;
